import React, { useState, useRef, useEffect } from 'react';
import { Modal, Box, IconButton, Typography } from '@mui/material';
import { Close, ZoomIn, ZoomOut } from '@mui/icons-material';

function ImageModal({ open, onClose, image }) {
    const [zoom, setZoom] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [dragStartImagePosition, setDragStartImagePosition] = useState({ x: 0, y: 0 });
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [lastPinchDistance, setLastPinchDistance] = useState(null);
    const containerRef = useRef(null);
    const wheelTimeoutRef = useRef(null);
    const pendingZoomRef = useRef(null);
    const scrollSpeed = 1 / zoom;
    const MAX_ZOOM = 10;
    const MIN_ZOOM = 1;

    const handleClose = () => {
        // Cancel any pending wheel animations
        if (wheelTimeoutRef.current) {
            cancelAnimationFrame(wheelTimeoutRef.current);
            wheelTimeoutRef.current = null;
        }
        pendingZoomRef.current = null;
        
        setZoom(1);
        setImagePosition({ x: 0, y: 0 });
        onClose();
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (wheelTimeoutRef.current) {
                cancelAnimationFrame(wheelTimeoutRef.current);
            }
        };
    }, []);

    const zoomIn = () => {
        const newZoom = Math.min(zoom + 0.25, MAX_ZOOM);
        applyZoom(newZoom);
    };

    const zoomOut = () => {
        const newZoom = Math.max(zoom - 0.25, MIN_ZOOM);
        applyZoom(newZoom);
    };

    // Helper function to get coordinates from mouse or touch event
    const getEventCoordinates = (e) => {
        if (e.touches && e.touches.length > 0) {
            // Touch event
            return {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY
            };
        }
        // Mouse event
        return {
            clientX: e.clientX,
            clientY: e.clientY
        };
    };

    // Simple unified zoom function
    const applyZoom = (targetZoom, focusX = null, focusY = null, currentZoom = zoom, currentPos = imagePosition) => {
        if (targetZoom === currentZoom) return;
        
        setZoom(targetZoom);
        
        // If zooming to 100%, reset position
        if (targetZoom <= 1) {
            setImagePosition({ x: 0, y: 0 });
            return;
        }
        
        // If no focus point provided, keep current position
        if (focusX === null || focusY === null || !containerRef.current) {
            return;
        }
        
        // Calculate new position to keep focus point fixed
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const centerX = containerRect.width / 2;
        const centerY = containerRect.height / 2;
        
        const offsetX = focusX - centerX;
        const offsetY = focusY - centerY;
        const zoomRatio = targetZoom / currentZoom;
        const scaleChange = (zoomRatio - 1);
        
        setImagePosition({
            x: currentPos.x - (offsetX * scaleChange) / targetZoom,
            y: currentPos.y - (offsetY * scaleChange) / targetZoom
        });
    };

    // Helper function to calculate distance between two touches
    const getTouchDistance = (touch1, touch2) => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Helper function to get center point between two touches
    const getTouchCenter = (touch1, touch2, containerRect) => {
        const centerX = (touch1.clientX + touch2.clientX) / 2 - containerRect.left;
        const centerY = (touch1.clientY + touch2.clientY) / 2 - containerRect.top;
        return { centerX, centerY };
    };

    const handlePointerDown = (e) => {
        // Handle pinch-to-zoom (two touches)
        if (e.touches && e.touches.length === 2) {
            const distance = getTouchDistance(e.touches[0], e.touches[1]);
            setLastPinchDistance(distance);
            setIsDragging(false); // Stop any ongoing drag
            e.preventDefault();
            return;
        }
        
        // Handle single touch/click dragging
        if (e.touches && e.touches.length !== 1) return;
        
        const coords = getEventCoordinates(e);
        setIsDragging(true);
        setLastPinchDistance(null); // Reset pinch state
        setDragStart({
            x: coords.clientX,
            y: coords.clientY
        });
        setDragStartImagePosition({
            x: imagePosition.x,
            y: imagePosition.y
        });
        e.preventDefault();
    };

    const handlePointerMove = (e) => {
        // Handle pinch-to-zoom (two touches)
        if (e.touches && e.touches.length === 2 && lastPinchDistance !== null) {
            const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
            const scaleChange = currentDistance / lastPinchDistance;
            
            // Calculate new zoom level
            const newZoom = Math.min(Math.max(zoom * scaleChange, MIN_ZOOM), MAX_ZOOM);
            
            if (!containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const { centerX, centerY } = getTouchCenter(e.touches[0], e.touches[1], containerRect);
            
            applyZoom(newZoom, centerX, centerY);
            setLastPinchDistance(currentDistance);
            e.preventDefault();
            return;
        }
        
        // Handle single touch/click dragging
        if (!isDragging) return;
        if (e.touches && e.touches.length !== 1) return;
        
        const coords = getEventCoordinates(e);
        const deltaX = (coords.clientX - dragStart.x) * scrollSpeed;
        const deltaY = (coords.clientY - dragStart.y) * scrollSpeed;
        setImagePosition({
            x: dragStartImagePosition.x + deltaX,
            y: dragStartImagePosition.y + deltaY
        });
        e.preventDefault();
    };

    const handlePointerUp = (e) => {
        // Reset pinch state when lifting fingers
        if (e.touches && e.touches.length < 2) {
            setLastPinchDistance(null);
        }
        
        // Stop dragging when no touches or mouse up
        if (!e.touches || e.touches.length === 0) {
            setIsDragging(false);
        }
    };

    const handleWheel = (e) => {
        e.preventDefault();
        
        if (!containerRef.current) return;
        
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const cursorX = e.clientX - containerRect.left;
        const cursorY = e.clientY - containerRect.top;
        
        // Calculate new zoom level
        const delta = e.deltaY;
        const zoomStep = Math.abs(delta) > 100 ? 0.25 : 0.1;
        const newZoom = delta < 0 
            ? Math.min(zoom + zoomStep, MAX_ZOOM)
            : Math.max(zoom - zoomStep, MIN_ZOOM);
        
        // Store values for throttled execution
        pendingZoomRef.current = { 
            targetZoom: newZoom, 
            focusX: cursorX, 
            focusY: cursorY,
            currentZoom: zoom,
            currentPos: { x: imagePosition.x, y: imagePosition.y }
        };
        
        // Throttle with requestAnimationFrame
        if (wheelTimeoutRef.current) {
            cancelAnimationFrame(wheelTimeoutRef.current);
        }
        
        wheelTimeoutRef.current = requestAnimationFrame(() => {
            if (pendingZoomRef.current) {
                const { targetZoom, focusX, focusY, currentZoom, currentPos } = pendingZoomRef.current;
                applyZoom(targetZoom, focusX, focusY, currentZoom, currentPos);
                pendingZoomRef.current = null;
            }
        });
    };

    if (!image) return null;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: '95vw',
                    maxHeight: '95vh',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    outline: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Modal Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                    }}
                >
                    <Typography variant="h6" sx={{ flex: 1, mr: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {image.name || 'Image Details'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton 
                            onClick={zoomOut} 
                            disabled={zoom <= MIN_ZOOM}
                            size="small"
                            title="Zoom Out"
                        >
                            <ZoomOut />
                        </IconButton>
                        
                        <Typography 
                            variant="body2"
                            sx={{ 
                                minWidth: '60px',
                                textAlign: 'center',
                                fontFamily: 'monospace'
                            }}
                        >
                            {Math.round(zoom * 100)}%
                        </Typography>
                        
                        <IconButton 
                            onClick={zoomIn} 
                            disabled={zoom >= MAX_ZOOM}
                            size="small"
                            title="Zoom In"
                        >
                            <ZoomIn />
                        </IconButton>
                        
                        <IconButton onClick={handleClose} size="small" title="Close">
                            <Close />
                        </IconButton>
                    </Box>
                </Box>

                {/* Modal Image Container */}
                <Box
                    ref={containerRef}
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        p: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                        maxHeight: 'calc(95vh - 80px)', // Account for header
                        cursor: isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                    }}
                    onMouseDown={handlePointerDown}
                    onMouseMove={handlePointerMove}
                    onMouseUp={handlePointerUp}
                    onMouseLeave={handlePointerUp}
                    onTouchStart={handlePointerDown}
                    onTouchMove={handlePointerMove}
                    onTouchEnd={handlePointerUp}
                    onTouchCancel={handlePointerUp}
                    onWheel={handleWheel}
                >
                    <img
                        src={image.presigned_url}
                        alt={image.name || 'Image'}
                        style={{
                            maxWidth: zoom <= 1 ? '100%' : 'none',
                            maxHeight: zoom <= 1 ? '100%' : 'none',
                            transform: `scale(${zoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                            transformOrigin: 'center center',
                            objectFit: 'contain',
                            display: 'block',
                            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                            pointerEvents: 'none', // Prevent image dragging
                        }}
                        draggable={false}
                    />
                </Box>

                {/* Instructions */}
                <Box
                    sx={{
                        p: 1,
                        bgcolor: 'background.default',
                        borderTop: 1,
                        borderColor: 'divider',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Use zoom controls, scroll wheel, or pinch to zoom • Click/touch and drag to pan • ESC or click outside to close
                    </Typography>
                </Box>
            </Box>
        </Modal>
    );
}

export default ImageModal;